// packages import
import { Box, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { FC, useEffect, useRef, useState } from "react";
// component import
import { CustomPagination } from "../common/CustomPagination";
import { StickyTable } from "../common/StickyTable";
import { UsersMenu } from '../menus/Users';
import { UsersSkeleton } from "../skeletons/UsersSkeleton";
import { PrintTasksModal } from './PrintTasksModal';
// others
import { Maybe, User, UserStatus } from "../../../generated";
import { PendingIcon, VerifiedIcon } from "../../assets/images";
import { LOCKED, NOT_APPLICABLE, STANDARD_DATE_FORMAT, USERS_TABLE_HEADERS } from "../../constants";
import { UserProps } from "../../interfaceTypes";
import { StatusBox } from "../../theme/styleComponents";
import { formatDateTime, titleize } from "../../utils";

export const UserListing: FC<UserProps> = ({ state, loading, searchLoader, dispatch: userDispatch }) => {
  const { usersData } = state || {}
  const scrollRef = useRef<HTMLDivElement>(null)
  const [selectedUser, setSelectedUser] = useState<Maybe<User> | undefined>(null)

  const scrollToTop = () => {
    scrollRef.current?.scrollIntoView({ block: "end", inline: "start" });
  }

  useEffect(() => {
    scrollToTop()
  }, [usersData]);

  return (
    <>
      <StickyTable pagination={<CustomPagination loading={loading || false} />}
        height='348px'
      >
        <TableHead>
          <TableRow>
            {USERS_TABLE_HEADERS.map((item, index) => (
              <TableCell
                component="th"
                align={index === USERS_TABLE_HEADERS.length - 1 ? "center" : 'left'}
                key={index}
                sx={{ minWidth: index === USERS_TABLE_HEADERS.length - 2 ? "142px" : 'inherit' }}
              >
                {item}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {loading || searchLoader ?
          <UsersSkeleton noOfRows={6} />
          :
          <TableBody>
            <Box ref={scrollRef} />

            {usersData?.map(user => {
              const { id, firstName, lastName, email, status, roles, emailVerified, createdAt, initials, lockedAt } = user || {}
              const createdDate = createdAt && moment(parseInt(createdAt)).format(STANDARD_DATE_FORMAT)
              const statusTextColor = status === UserStatus.Active ? 'green' : 'red'

              return (
                <TableRow key={id} hover>
                  <TableCell>
                    <Box maxWidth='250px'>
                      <Tooltip title={`${firstName || '--'} ${lastName}`} arrow placement='top-start'>
                        <Typography variant="body1" noWrap textTransform='capitalize'>
                          {`${firstName || '--'} ${lastName}`}
                        </Typography>
                      </Tooltip>

                      <Tooltip title={email} arrow>
                        <Typography variant="body2" noWrap fontWeight='normal' color='wardGray'>
                          {email}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body1" textTransform='uppercase'>{initials || NOT_APPLICABLE}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body1">{createdDate}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body1">
                      {roles?.map(role => titleize(role?.role || ''))}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box display='flex' gap='8px' alignItems='center'>
                      <StatusBox borderColor={statusTextColor}>{titleize(status || '')}</StatusBox>

                      {/* Too many failed sign-ins; only an admin's unlock clears it. */}
                      {lockedAt && (
                        <Tooltip title={`Locked on ${formatDateTime(lockedAt)}`} arrow>
                          <StatusBox borderColor='red'>{LOCKED}</StatusBox>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box display='flex' gap='10px' alignItems='center'>
                      {emailVerified ? <VerifiedIcon /> : <PendingIcon />}
                      <Typography variant='body2'>{emailVerified ? 'Verified' : 'Pending'}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell align='center'>
                    <UsersMenu currentUser={user} userDispatch={userDispatch} setSelectedUser={setSelectedUser} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        }
      </StickyTable>
      <PrintTasksModal selectedUser={selectedUser} />
    </>
  )
}